import {isMobile} from "react-device-detect";
import {useEffect, useRef, useState} from "react";
import MapState from "../../pages/ward/map/mapState";

export const ORIGIN = Object.freeze({x: 0, y: 0});
const SCROLL_SENSITIVITY = 0.0005;
const MAX_ZOOM = 2.5;
const MIN_ZOOM = 0.1;

// adjust to device to avoid blur
const {devicePixelRatio: ratio = 1} = window;

const AgylaCanvas = ({
                         id = "canvas-agyle",
                         width = 300,
                         height = 300,
                         reference,
                         reDraw,
                         moveWithCtrl = false,
                         trackPointer,
                         holdDrag = false,
                         wheelZoom = true,
                         zoomRef
                     }) => {
    // Canvas
    const [dragging, setDragging] = useState(false);
    const pinch = useRef(false);
    const pinchCords = useRef(new Array(4));
    const touch = useRef(ORIGIN);

    const pointerPos = useRef(ORIGIN);

    useEffect(() => {
        if (reference.current) {
            draw();
            reference.current.addEventListener("pointerdown", onTrackPointer, false);
            document.addEventListener("pointermove", onTrackPointer, false);
        }
        return () => {
            if (reference.current) {
                reference.current.removeEventListener("pointerdown", onTrackPointer, false);
                document.removeEventListener("pointermove", onTrackPointer, false);
            }
        };
    }, [reference.current]);

    const draw = () => {
        if (reference.current) {
            const {width, height} = reference.current;
            const context = reference.current.getContext("2d");
            // Set canvas dimensions

            // context.save();
            // context.setTransform(1, 0, 0, 1, 0, 0);
            // context.clearRect(0, 0, width, height);
            // context.restore();

            reDraw();
        }
    };

    const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

    const getTransformedPoint = (x, y) => {
        const ctx = reference?.current?.getContext("2d");
        if (ctx) {
            const originalPoint = new DOMPoint(x, y);
            return ctx.getTransform().invertSelf().transformPoint(originalPoint);
        }
    };

    const handlePointerDown = (event) => {
        const {clientX, clientY} = event;
        const rect = reference.current.getBoundingClientRect();
        const currentTransformedCursor = getTransformedPoint(event.pageX - rect.left, event.pageY - rect.top);
        touch.current = {x: currentTransformedCursor.x, y: currentTransformedCursor.y};

        // Pinch Mobile
        pinch.current = !event.isPrimary;
        pinchCords.current[event.isPrimary ? 0 : 1] = {x: clientX, y: clientY};

        if (ctrlMove(event) || isMobile) setDragging(true);
    };

    const handlePointerMove = (event) => {
        const {clientX, clientY} = event;
        const rect = reference.current.getBoundingClientRect();
        const currentTransformedCursor = getTransformedPoint(event.pageX - rect.left, event.pageY - rect.top);
        const context = reference.current.getContext("2d");

        if (dragging && !pinch.current && !holdDrag) {
            context.translate(currentTransformedCursor.x - touch.current.x, currentTransformedCursor.y - touch.current.y);
            draw();
        }

        // If there is more than one pointer, start pinch method
        if (pinch.current && !holdDrag) {
            pinchCords.current[event.isPrimary ? 2 : 3] = {x: clientX, y: clientY};
            handlePinchZoom(event);
        }

        // Track only first pointer
        if (event.isPrimary) {
            pointerPos.current = {
                x: clientX - rect.left,
                y: clientY - rect.top,
            };
        }
    };

    const handlePointerUp = () => {
        pinchCords.current = new Array(4);
        setDragging(false);
    };

    const handlePinchZoom = (event) => {
        const startLen = Math.round(
            Math.sqrt(
                Math.pow((pinchCords.current[1]?.x - pinchCords.current[0]?.x), 2) +
                Math.pow((pinchCords.current[1]?.y - pinchCords.current[0]?.y), 2),
            ),
        );
        const len = Math.round(
            Math.sqrt(
                Math.pow((pinchCords.current[3]?.x - pinchCords.current[2]?.x), 2) +
                Math.pow((pinchCords.current[3]?.y - pinchCords.current[2]?.y), 2),
            ),
        );
        const zooming = len > startLen ? -25 : 25;

        pinchCords.current[0] = pinchCords.current[2];
        pinchCords.current[1] = pinchCords.current[3];


        const context = reference.current.getContext("2d");
        const rect = reference.current.getBoundingClientRect();
        const currentTransformedCursor = getTransformedPoint(event.pageX - rect.left, event.pageY - rect.top);
        const zoom = clamp(1 + zooming * SCROLL_SENSITIVITY * -1, MIN_ZOOM, MAX_ZOOM);

        context.translate(currentTransformedCursor.x, currentTransformedCursor.y);
        context.scale(zoom, zoom);
        context.translate(-currentTransformedCursor.x, -currentTransformedCursor.y);

        setNewZoom(zoom);

        draw();
    };

    const setNewZoom = (zoom) => {
        if(zoomRef) {
            zoomRef.current = zoomRef.current * zoom;
        } else {
            MapState.zoom = MapState.zoom * zoom;
        }
    }
    const handleWheel = (event) => {
        const {deltaY} = event;
        if (!dragging && !holdDrag) {
            if (wheelZoom) {
                const context = reference.current.getContext("2d");
                const rect = reference.current.getBoundingClientRect();
                const currentTransformedCursor = getTransformedPoint(event.pageX - rect.left, event.pageY - rect.top);
                const zoom = clamp(1 + deltaY * SCROLL_SENSITIVITY * -1, MIN_ZOOM, MAX_ZOOM);

                context.translate(currentTransformedCursor.x, currentTransformedCursor.y);
                context.scale(zoom, zoom);
                context.translate(-currentTransformedCursor.x, -currentTransformedCursor.y);

                setNewZoom(zoom);

                draw();
            } else {

                const context = reference.current.getContext("2d");
                context.translate(0, -deltaY);

                draw();

            }
        }
    };

    const ctrlMove = (e) => {
        return moveWithCtrl ? e.ctrlKey : true;
    };

    const onTrackPointer = (e) => {
        if (trackPointer && reference.current) {
            const ctx = reference.current.getContext("2d");
            const rect = reference.current.getBoundingClientRect();
            const originalPoint = new DOMPoint(e.pageX - rect.left, e.pageY - rect.top);
            const {x, y} = ctx.getTransform().invertSelf().transformPoint(originalPoint);

            trackPointer(x, y);
        }
    };

    return (
        <canvas
            id={id}
            width={width}
            height={height}
            ref={reference}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerUp}
            onWheel={handleWheel}
        >
            Your browser does not support HTML5 Canvas.
        </canvas>
    );
};

export default AgylaCanvas;